import { useMemo } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import {
	AsyncResult,
	PaginatedDataResult,
	useContextStore,
	useMappedStore,
	usePaginatedStore,
	useStoredData,
} from "@utils/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { GroupMemberRole } from "@domain/group/group";
import { GroupMember } from "@domain/group/groupMember";
import { QuizParticipant } from "@domain/lms/quiz/quiz";
import { OrganizationMemberRole } from "@domain/organization/organization";
import { Profile } from "./profile";
import { Person } from "./user";

export function useUserProfile(id: string, slug?: string): AsyncResult<Profile> {
	const { userService } = useServices();
	const currentProfile = useCurrentProfile();
	return useStoredData(slug ?? id, slug ? userService.slugProfilesStore : userService.profilesStore, [
		currentProfile?.id,
	]);
}

export function useUserPerson(id: string, slug?: string): AsyncResult<Person> {
	const { userService } = useServices();
	const currentProfile = useCurrentProfile();
	return useStoredData(slug ?? id, slug ? userService.slugProfilesStore : userService.profilesStore, [
		currentProfile?.id,
	]);
}

export function useSuggestedProfiles(count: number): PaginatedDataResult<Profile> {
	const { userService } = useServices();
	const store = useMemo(
		() => new StorePresentation(userService.profilesStore, () => userService.suggest(count)),
		[count]
	);
	return usePaginatedStore(store, [count]);
}

export function useFollowers(profileId: string): PaginatedDataResult<Profile> {
	const { userService } = useServices();
	return useMappedStore(profileId, userService.followersStore, [profileId]);
}

export function useFollowees(profileId: string): PaginatedDataResult<Profile> {
	const { userService } = useServices();
	return useMappedStore(profileId, userService.followeesStore, [profileId]);
}

export function useOrganizationMembers(
	organizationId: string,
	role: OrganizationMemberRole
): PaginatedDataResult<Profile> {
	const { userService } = useServices();
	const currentProfile = useCurrentProfile();
	return useMappedStore(organizationId, userService.getOrganizationMemberStore(role), [
		organizationId,
		role,
		currentProfile?.id,
	]);
}

export function useGroupMember(groupId: string, memberId: string): AsyncResult<GroupMember> {
	const { userService } = useServices();
	return useContextStore(groupId, memberId, userService.groupMembersStore);
}

export function useGroupMembers(groupId: string, role: GroupMemberRole | "pending"): PaginatedDataResult<GroupMember> {
	const { userService } = useServices();
	const currentProfile = useCurrentProfile();
	return useMappedStore(groupId, userService.getGroupMemberStore(role), [groupId, role, currentProfile?.id]);
}
export function useShareroomLastFiveMembers(groupId: string): PaginatedDataResult<GroupMember> {
	const { userService } = useServices();
	return useMappedStore(groupId, userService.getShareroomLastFiveMembers(), [groupId]);
}

export function useOrganizationFollowers(
	organizationId: string,
	role: OrganizationMemberRole
): PaginatedDataResult<Profile> {
	const { userService } = useServices();
	const currentProfile = useCurrentProfile();
	return useMappedStore(organizationId, userService.getOrganizationMemberStore(role), [
		organizationId,
		role,
		currentProfile?.id,
	]);
}

export function useMyQuizzesParticipants(quizId: string): PaginatedDataResult<QuizParticipant> {
	const { userService } = useServices();
	return useMappedStore(quizId, userService.quizParticipantsStore, [quizId]) as PaginatedDataResult<QuizParticipant>;
}
